import { panoramaBracingSpace } from './panoramaBracingSpace';
import type { BuildInput } from "../../engines/types";
import { roofFramePositions } from "../roof/roofFramePositions";

/** References describe their own systems; none certifies our configurable model. */
export const CONSTRUCTION_SOURCES = {
	roofStructure: {
		title: "TräGuiden – Takstolens uppbyggnad",
		url: "https://www.traguiden.se/konstruktion/takstolshandboken/bakgrund/1.1-introduktion/1.1-introduktion/",
	},
	referenceShed: {
		title: "Svenskt Trä – Enkelboden",
		url: "https://www.byggbeskrivningar.se/utvandigt/enkelboden/",
	},
	roofMoisture: {
		title: "TräGuiden – Fuktskydd för tak",
		url: "https://www.traguiden.se/konstruktion/konstruktiv-utformning/stomkomplettering/tak/fuktskydd-for-tak/",
	},
	insulatedRoof: {
		title: "TräGuiden – Konstruktionsexempel tak",
		url: "https://www.traguiden.se/konstruktion/konstruktionsexempel/tak/",
	},
	connectors: {
		title: "Simpson Strong-Tie – Takåsfäste SPF",
		url: "https://www.strongtie.se/sv-SE/produkter/takasfaste-spf",
	},
	saunaFoil: {
		title: "Harvia – SAS10001",
		url: "https://www.harvia.com/fi/tuotteet/SAS10001/alumiinipaperi-12530-m2",
	},
	indoorSauna: {
		title: "ISOVER – Bygga bastu",
		url: "https://www.isover.se/supporten-tipsar/bygga-bastu",
	},
	benchMounting: {
		title: "Svenskt Trä – Bastulavar",
		url: "https://www.byggbeskrivningar.se/invandigt/bastu/",
	},
} as const;
export type ConstructionSourceId = keyof typeof CONSTRUCTION_SOURCES;

export type ConstructionCheckId =
	| "roofLoadPath"
	| "connections"
	| "roofMoisture"
	| "saunaEnvelope"
	| "benchMounting"
	| "floorWater";
export interface ConstructionCheck {
	id: ConstructionCheckId;
	status: "detail_required" | "system_required";
	sources: ConstructionSourceId[];
}
export interface ConstructionConnection {
	id: "roofBearing" | "ridge" | "baseAnchorage";
	/** Counts connection locations; never imply this is a count of brackets or screws. */
	locations: number | null;
	specification: "not_selected";
	includedInPrice: false;
	sources: ConstructionSourceId[];
}
export interface ConstructionBasis {
	checkedAt: "2026-09-05";
	status: "planning_basis";
	readyToBuild: false;
	roofKind: "gable" | "mono";
	includesShower: boolean;
	roofFrames: number;
	connections: ConstructionConnection[];
	checks: ConstructionCheck[];
	panoramaBracing?: ReturnType<typeof panoramaBracingSpace>;
}

export function buildConstructionBasis(
	input: Pick<BuildInput, "dimensions" | "roofConfig" | "thermalInputs"> & Partial<Pick<BuildInput, "openings" | "glazingStyle">>,
): ConstructionBasis | undefined {
	if (!input.roofConfig || input.roofConfig.kind === "barrel") return undefined;
	const roofFrames = roofFramePositions(input.dimensions.length).length;
	return {
		checkedAt: "2026-09-05",
		status: "planning_basis",
		readyToBuild: false,
		roofKind: input.roofConfig.kind,
		includesShower: input.thermalInputs.includesShower,
		roofFrames,
		panoramaBracing: input.glazingStyle === "panorama" && input.openings
			? panoramaBracingSpace(input.dimensions, input.openings) : undefined,
		connections: [
			{
				id: "roofBearing",
				locations: roofFrames * 2,
				specification: "not_selected",
				includedInPrice: false,
				sources: ["connectors", "referenceShed"],
			},
			...(input.roofConfig.kind === "gable"
				? [
						{
							id: "ridge" as const,
							locations: roofFrames,
							specification: "not_selected" as const,
							includedInPrice: false as const,
							sources: ["roofStructure" as const],
						},
					]
				: []),
			{
				id: "baseAnchorage",
				locations: null,
				specification: "not_selected",
				includedInPrice: false,
				sources: ["referenceShed"],
			},
		],
		checks: [
			{
				id: "roofLoadPath",
				status: "system_required",
				sources: ["roofStructure", "referenceShed"],
			},
			{ id: "connections", status: "detail_required", sources: ["connectors"] },
			{ id: "benchMounting", status: "detail_required", sources: ["benchMounting"] },
			{
				id: "roofMoisture",
				status: "system_required",
				sources: ["roofMoisture", "insulatedRoof"],
			},
			{
				id: "saunaEnvelope",
				status: "detail_required",
				sources: ["saunaFoil", "indoorSauna"],
			},
			{
				id: "floorWater",
				status: input.thermalInputs.includesShower
					? "system_required"
					: "detail_required",
				sources: [],
			},
		],
	};
}
